import type { IRect } from '@nova/types';

import { clampInt } from '../math-utils';
import { findNearestFreeRectOrigin, rectOverlapsAny } from '../rect-utils';

/** Child rectangle authored relative to a parent rectangle (0-indexed upper-left). */
export interface NestedRectChildInput extends IRect {
  id: string;
}

/** Child after size/origin clamping and optional relocation away from earlier children. */
export interface NestedRectChildResult extends NestedRectChildInput {
  clamped: boolean;
  relocated: boolean;
}

export interface ResolveNestedRectsArgs {
  parentWidth: number;
  parentHeight: number;
  children: readonly NestedRectChildInput[];
}

export type ResolveNestedRectsResult = {
  ok: true;
  parentWidth: number;
  parentHeight: number;
  children: NestedRectChildResult[];
} | {
  ok: false;
  reason: string;
};

/**
 * Places child rectangles inside a parent in input order.
 * Each child is size-clamped into the parent, then origin-clamped so the full footprint fits.
 * Later children that still overlap an earlier child are relocated to the nearest free origin.
 * Fails when child areas sum past the parent area, or when no non-overlapping origin remains.
 */
export function resolveNestedRects(args: ResolveNestedRectsArgs): ResolveNestedRectsResult {
  const parentWidth = Math.max(1, Math.floor(args.parentWidth));
  const parentHeight = Math.max(1, Math.floor(args.parentHeight));
  const parentArea = parentWidth * parentHeight;

  const normalizedChildren: NestedRectChildInput[] = args.children.map((child) => ({
    id: child.id,
    x: Math.floor(child.x),
    y: Math.floor(child.y),
    width: Math.max(1, Math.floor(child.width)),
    height: Math.max(1, Math.floor(child.height))
  }));

  let childAreaSum = 0;
  for (const child of normalizedChildren) {
    childAreaSum += Math.min(child.width, parentWidth) * Math.min(child.height, parentHeight);
  }

  if (childAreaSum > parentArea) {
    return {
      ok: false,
      reason: `Child footprints (${childAreaSum}) exceed parent area (${parentArea}).`
    };
  }

  const placed: NestedRectChildResult[] = [];

  for (const child of normalizedChildren) {
    const width = Math.min(child.width, parentWidth);
    const height = Math.min(child.height, parentHeight);
    const maxOriginX = parentWidth - width;
    const maxOriginY = parentHeight - height;
    const x = clampInt(child.x, 0, maxOriginX);
    const y = clampInt(child.y, 0, maxOriginY);
    const clamped = x !== child.x
      || y !== child.y
      || width !== child.width
      || height !== child.height;

    const blocked: IRect[] = placed.map((prior) => ({
      x: prior.x,
      y: prior.y,
      width: prior.width,
      height: prior.height
    }));

    const candidate: IRect = { x, y, width, height };
    if (!rectOverlapsAny(candidate, blocked)) {
      placed.push({
        id: child.id,
        ...candidate,
        clamped,
        relocated: false
      });
      continue;
    }

    const relocatedOrigin = findNearestFreeRectOrigin({
      width,
      height,
      maxOriginX,
      maxOriginY,
      preferred: { x, y },
      blocked
    });

    if (!relocatedOrigin) {
      return {
        ok: false,
        reason: `No non-overlapping placement remains inside the parent for child "${child.id}".`
      };
    }

    placed.push({
      id: child.id,
      x: relocatedOrigin.x,
      y: relocatedOrigin.y,
      width,
      height,
      clamped,
      relocated: true
    });
  }

  return {
    ok: true,
    parentWidth,
    parentHeight,
    children: placed
  };
}
